// Use Web Crypto API which is available in modern browsers

// Get encryption key from environment variables using Vite's import.meta.env
const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY;

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 12 bytes for GCM is recommended

// Convert a string to an ArrayBuffer
const stringToArrayBuffer = (str: string): ArrayBuffer => {
  const encoder = new TextEncoder();
  return encoder.encode(str).buffer;
};

// Convert an ArrayBuffer to a hex string
const arrayBufferToHex = (buffer: ArrayBuffer): string => {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// Convert a hex string to an ArrayBuffer
const hexToArrayBuffer = (hex: string): ArrayBuffer => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes.buffer;
};

// Derive a key from a password using PBKDF2
const deriveKey = async (password: string, salt: ArrayBuffer, iterations: number = 100000): Promise<CryptoKey> => {
  const encoder = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
};

export class EncryptionService {
  static async encrypt(text: string): Promise<string> {
    try {
      // Generate a random salt
      const salt = window.crypto.getRandomValues(new Uint8Array(16));
      // Generate a random IV
      const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
      
      // Derive the key from the password and salt
      const key = await deriveKey(ENCRYPTION_KEY, salt);
      
      // Convert the text to encrypt to an ArrayBuffer
      const encoder = new TextEncoder();
      const data = encoder.encode(text);
      
      // Encrypt the data
      const encryptedData = await window.crypto.subtle.encrypt(
        { name: ALGORITHM, iv },
        key,
        data
      );
      
      // Combine salt, iv, and encrypted data into a single ArrayBuffer
      const combined = new Uint8Array(salt.byteLength + iv.byteLength + encryptedData.byteLength);
      combined.set(new Uint8Array(salt), 0);
      combined.set(new Uint8Array(iv), salt.byteLength);
      combined.set(new Uint8Array(encryptedData), salt.byteLength + iv.byteLength);
      
      // Convert to hex string for storage
      return arrayBufferToHex(combined.buffer);
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  static async decrypt(encryptedHex: string): Promise<string> {
    try {
      // Convert hex string back to ArrayBuffer
      const combined = hexToArrayBuffer(encryptedHex);
      const view = new DataView(combined);
      
      // Extract salt (first 16 bytes)
      const salt = combined.slice(0, 16);
      // Extract IV (next IV_LENGTH bytes)
      const iv = combined.slice(16, 16 + IV_LENGTH);
      // Extract encrypted data (the rest)
      const encryptedData = combined.slice(16 + IV_LENGTH);
      
      // Derive the same key using the salt
      const key = await deriveKey(ENCRYPTION_KEY, salt);
      
      // Decrypt the data
      const decryptedData = await window.crypto.subtle.decrypt(
        { name: ALGORITHM, iv: new Uint8Array(iv) },
        key,
        encryptedData
      );
      
      // Convert the decrypted data to a string
      const decoder = new TextDecoder();
      return decoder.decode(decryptedData);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  // Helper method to check if a string is encrypted
  static isEncrypted(text: string): boolean {
    // Check if the text is a hex string with at least salt + IV length
    return /^[0-9a-fA-F]+$/.test(text) && text.length > 32 + (IV_LENGTH * 2);
  }
}